import React from "react";
import PropTypes from "prop-types";
import classes from "./Sidebar.module.scss";
import { Link, Route, Switch, useLocation } from "react-router-dom";
import { remindersEnabled } from "../../flags";
import teamCareUrl from "util/url";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faUser,
  faQuestionCircle
} from "@fortawesome/free-solid-svg-icons";
import huronLogo from "@global-products/styles/images/huron-logo.svg";
import { ReactComponent as MyQueryIcon } from "assets/img/sidebar/my-query-icon.svg";
import { ReactComponent as CollapseIcon } from "assets/img/sidebar/collapse-icon.svg";
import { ReactComponent as AwareIcon } from "assets/img/sidebar/aware.svg";
import { ReactComponent as DashboardIcon } from "assets/img/sidebar/dashboard.svg";
import { ReactComponent as QueryIcon } from "assets/img/sidebar/query.svg";
import { ReactComponent as CareIcon } from "assets/img/sidebar/care.svg";
import { ReactComponent as TeamIcon } from "assets/img/sidebar/team.svg";
import { ReactComponent as MyDashboard } from "assets/img/sidebar/my-dashboard.svg";
import { ReactComponent as SharedDashboard } from "assets/img/sidebar/shared-dashboard.svg";
import { ReactComponent as HuronVerticalLogo } from "assets/img/sidebar/huron-vertical-logo.svg";
import { ReactComponent as EnvelopeIcon } from "assets/img/sidebar/envelope.svg";
import { ReactComponent as CalendorUserIcon } from "assets/img/sidebar/calendor-user-icon.svg";
import { ReactComponent as BrandIcon } from "assets/img/sidebar/brand-icon.svg";

import { OverlayTrigger, Tooltip } from "react-bootstrap";
/* eslint jsx-a11y/anchor-is-valid: 0 */

/**
 * Pure component that displays the Sidebar component
 *
 * @version 0.0.1
 * @author Steve Hunley
 *
 */

const { careUrl, teamUrl } = teamCareUrl();

const SIDEBAR_ROUTES = {
  DASHBOARD: [
    "/dashboards",
    "/dashboardVisualization",
    "/dashboardVisualization/:dashboardId"
  ],

  QUERY: ["/openquery", "/queryvisualization"],

  AWARE: ["/aware"],

  BRAND: ["/UserBrands", "/BrandAdmin", "/BrandAdmin/:brandId"],

  USER: [
    "/UserAdmin",
    "/UserAdmin/:userId",
    "/User"
  ],

  ACCOUNT: ["/UserAccounts", "/AccountAdmin", "/ContractAdmin"],
  MFA_RECOVERY: ["/admin/mfa-recovery"],

  HELP_DOC: ["/help"]
};

const COMMON_SIDEBAR_ROUTES = [
  ...SIDEBAR_ROUTES.HELP_DOC,
  ...SIDEBAR_ROUTES.USER,
  ...SIDEBAR_ROUTES.ACCOUNT,
  ...SIDEBAR_ROUTES.BRAND,
  ...SIDEBAR_ROUTES.MFA_RECOVERY
];

const useIsActive = (location) => {
  return (...paths) =>
    paths.some(
      path =>
        location.pathname === path || location.pathname.startsWith(path + "/")
    );
};

const HuronLogoSidebar = ({ collapsed }) => {
  return (
    <>
      <Link to="/" title="Navigate to Home">
        {collapsed ? (
          <HuronVerticalLogo className={`${classes["huron-logo"]}`} />
        ) : (
          <img
            src={huronLogo}
            alt="Huron Logo"
            className={`${classes["huron-logo"]}`}
          />
        )}
      </Link>
    </>
  );
};

const ApplicationList = ({ collapsed }) => {
  const location = useLocation();
  const isActive = useIsActive(location);

  return (
    <nav className={classes.applicationsWrapper}>
      <div className={classes.heading}>APPLICATIONS</div>

      <ul className={classes.applicationList}>
        <li className={classes.item}>
          <a href={teamUrl} target="_blank" rel="noopener noreferrer">
            <OverlayTrigger
              placement="right"
              overlay={
                collapsed ? (
                  <Tooltip id="sidebar-tooltip">TEAM</Tooltip>
                ) : (
                  <></>
                )
              }
              trigger={collapsed ? ["hover", "focus"] : []}
            >
              <TeamIcon className={classes.svgIcon} />
            </OverlayTrigger>
            <span className={classes.text}>TEAM</span>
          </a>
        </li>

        <li
          className={`${classes.item} ${
            !location.state?.dashboards &&
            isActive(...SIDEBAR_ROUTES.QUERY)
              ? classes.active
              : ""
          }`}
        >
          <Link to="/openquery">
            <OverlayTrigger
              placement="right"
              overlay={
                collapsed ? (
                  <Tooltip id="sidebar-tooltip">QUERY</Tooltip>
                ) : (
                  <></>
                )
              }
              trigger={collapsed ? ["hover", "focus"] : []}
            >
              <QueryIcon className={classes.svgIcon} />
            </OverlayTrigger>

            <span className={classes.text}>QUERY</span>
          </Link>
        </li>

        <li
          className={`${classes.item} ${
            isActive(...SIDEBAR_ROUTES.AWARE) ? classes.active : ""
          }`}
        >
          <Link to="/aware">
            <OverlayTrigger
              placement="right"
              overlay={
                collapsed ? (
                  <Tooltip id="sidebar-tooltip">AWARE</Tooltip>
                ) : (
                  <></>
                )
              }
              trigger={collapsed ? ["hover", "focus"] : []}
            >
              <AwareIcon className={classes.svgIcon} />
            </OverlayTrigger>
            <span className={classes.text}>AWARE</span>
          </Link>
        </li>

        <li className={classes.item}>
          <a href={careUrl} target="_blank" rel="noopener noreferrer">
            <OverlayTrigger
              placement="right"
              overlay={
                collapsed ? <Tooltip id="sidebar-tooltip">CARE</Tooltip> : <></>
              }
              trigger={collapsed ? ["hover", "focus"] : []}
            >
              <CareIcon className={classes.svgIcon} />
            </OverlayTrigger>
            <span className={classes.text}>CARE</span>
          </a>
        </li>

        <li
          className={`${classes.item} ${
            location.state?.dashboards ||
            isActive(...SIDEBAR_ROUTES.DASHBOARD)
              ? classes.active
              : ""
          }`}
        >
          <Link to="/dashboards">
            <OverlayTrigger
              placement="right"
              overlay={
                collapsed ? (
                  <Tooltip id="sidebar-tooltip">Dashboards</Tooltip>
                ) : (
                  <></>
                )
              }
              trigger={collapsed ? ["hover", "focus"] : []}
            >
              <DashboardIcon className={classes.svgIcon} />
            </OverlayTrigger>
            <span className={classes.text}>DASHBOARDS</span>
          </Link>
        </li>
      </ul>
    </nav>
  );
};

const OpenQuerySidebar = ({ collapsed, onToggle, isMyQueriesActive }) => {
  return (
    <div
      className={`${classes["sidebar-wrapper"]} ${
        collapsed ? classes.collapsed : ""
      }`}
    >
      <HuronLogoSidebar collapsed={collapsed} />
      <span
        className={`${classes["toggle-icon"]} ${
          collapsed
            ? classes["expand-icon"]
            : classes["collapse-icon"]
        }`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <CollapseIcon />
      </span>
      <nav
        id="sidebar"
        className={`${classes["sidebar-nav"]} ${classes["sidebar-openquery"]}`}
      >
        <ul className={`list-unstyled ${classes["menu-items-list"]}`}>
          {remindersEnabled && (
            <li className={classes["sidebar-item"]}>
              <Link to={"/openquery/reminders"} className={classes["content"]}>
                <span className={classes["text"]}>My Reminders</span>
              </Link>
            </li>
          )}
          <li className={`${classes["sidebar-item"]} ${classes["active"]}`}>
            <Link to={"/openquery"} className={classes["content"]}>
              <span className={classes["icon"]}>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    collapsed ? (
                      <Tooltip id="sidebar-tooltip">Create New Query</Tooltip>
                    ) : (
                      <></>
                    )
                  }
                  trigger={collapsed ? ["hover", "focus"] : []}
                >
                  <FontAwesomeIcon icon={faPlus} />
                </OverlayTrigger>
              </span>
              <span className={classes["text"]}>Create New Query</span>
            </Link>
          </li>
          <li
            className={`${classes["sidebar-item"]} ${
              isMyQueriesActive ? classes["active-tab"] : ""
            }`}
          >
            <Link to={"/openquery/queries"} className={classes["content"]}>
              <span className={classes["icon"]}>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    collapsed ? (
                      <Tooltip id="sidebar-tooltip">My Queries</Tooltip>
                    ) : (
                      <></>
                    )
                  }
                  trigger={collapsed ? ["hover", "focus"] : []}
                >
                  <MyQueryIcon className={classes.svgIcon} />
                </OverlayTrigger>
              </span>
              <span className={classes["text"]}>My Queries</span>
            </Link>
          </li>
        </ul>
        <ApplicationList collapsed={collapsed} />
      </nav>
    </div>
  );
};

const CommonSidebar = ({ collapsed, onToggle, isActive }) => {
  return (
    <div
      className={`${classes["sidebar-wrapper"]} ${
        collapsed ? classes.collapsed : ""
      }`}
    >
      <HuronLogoSidebar collapsed={collapsed} />
      <span
        className={`${classes["toggle-icon"]} ${
          collapsed
            ? classes["expand-icon"]
            : classes["collapse-icon"]
        }`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <CollapseIcon />
      </span>
      <nav
        id="sidebar"
        className={`${classes["sidebar-nav"]} ${classes["sidebar-openquery"]}`}
      >
        <ul className={`list-unstyled ${classes["menu-items-list"]}`}>
          <li
            className={`${classes["sidebar-item"]} ${
              isActive(...SIDEBAR_ROUTES.BRAND)
                ? classes["active-tab"]
                : ""
            }`}
          >
            <Link to="/UserBrands" className={classes.content}>
              <span className={classes.icon}>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    collapsed ? (
                      <Tooltip id="sidebar-tooltip">Brands</Tooltip>
                    ) : (
                      <></>
                    )
                  }
                  trigger={collapsed ? ["hover", "focus"] : []}
                >
                  <BrandIcon className={classes.svgIcon} />
                </OverlayTrigger>
              </span>

              {!collapsed && <span className={classes.text}>Brands</span>}
            </Link>
          </li>
          <li
            className={`${classes["sidebar-item"]} ${
              isActive(
                ...SIDEBAR_ROUTES.ACCOUNT,
                ...SIDEBAR_ROUTES.MFA_RECOVERY
              )
                ? classes["active-tab"]
                : ""
            }`}
          >
            <Link to="/UserAccounts" className={classes.content}>
              <span className={classes.icon}>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    collapsed ? (
                      <Tooltip id="sidebar-tooltip">Account</Tooltip>
                    ) : (
                      <></>
                    )
                  }
                  trigger={collapsed ? ["hover", "focus"] : []}
                >
                  <CalendorUserIcon className={classes.svgIcon} />
                </OverlayTrigger>
              </span>

              {!collapsed && <span className={classes.text}>Account</span>}
            </Link>
          </li>
          <li
            className={`${classes["sidebar-item"]} ${
              isActive(...SIDEBAR_ROUTES.USER)
                ? classes["active-tab"]
                : ""
            }`}
          >
            <Link to="/User" className={classes.content}>
              <span className={classes.icon}>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    collapsed ? (
                      <Tooltip id="sidebar-tooltip">My User</Tooltip>
                    ) : (
                      <></>
                    )
                  }
                  trigger={collapsed ? ["hover", "focus"] : []}
                >
                  <FontAwesomeIcon icon={faUser} />
                </OverlayTrigger>
              </span>

              {!collapsed && <span className={classes.text}>My User</span>}
            </Link>
          </li>
          <li
            className={`${classes["sidebar-item"]} ${
              isActive(...SIDEBAR_ROUTES.HELP_DOC)
                ? classes["active-tab"]
                : ""
            }`}
          >
            <Link to="/help" className={classes.content}>
              <span className={classes.icon}>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    collapsed ? (
                      <Tooltip id="sidebar-tooltip">Help Documents</Tooltip>
                    ) : (
                      <></>
                    )
                  }
                  trigger={collapsed ? ["hover", "focus"] : []}
                >
                  <FontAwesomeIcon icon={faQuestionCircle} />
                </OverlayTrigger>
              </span>

              {!collapsed && (
                <span className={classes.text}>Help Documents</span>
              )}
            </Link>
          </li>
        </ul>
        <ApplicationList collapsed={collapsed} />
      </nav>
    </div>
  );
};

const MonitoringServicesSidebar = ({ collapsed, onToggle }) => {
  return (
    <div
      className={`${classes["sidebar-wrapper"]} ${
        collapsed ? classes.collapsed : ""
      }`}
    >
      <HuronLogoSidebar collapsed={collapsed} />
      <span
        className={`${classes["toggle-icon"]} ${
          collapsed
            ? classes["expand-icon"]
            : classes["collapse-icon"]
        }`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <CollapseIcon />
      </span>
      <nav
        id="sidebar"
        className={`${classes["sidebar-nav"]} ${classes["sidebar-openquery"]}`}
      >
        <ul className={`list-unstyled ${classes["menu-items-list"]}`}>
          <li className={`${classes["sidebar-item"]} ${classes["active-tab"]}`}>
            <Link to={"/aware"} className={classes["content"]}>
              <span className={classes["icon"]}>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    collapsed ? (
                      <Tooltip id="sidebar-tooltip">My Aware Reports</Tooltip>
                    ) : (
                      <></>
                    )
                  }
                  trigger={collapsed ? ["hover", "focus"] : []}
                >
                  <EnvelopeIcon className={classes.svgIcon} />
                </OverlayTrigger>
              </span>
              <span className={classes["text"]}>My Aware Reports</span>
            </Link>
          </li>
        </ul>
        <ApplicationList collapsed={collapsed} />
      </nav>
    </div>
  );
};

const MyFilesSidebar = () => {
  return (
    <div className={classes.sidebar}>
      <nav id="sidebar" className={classes["sidebar-nav"]}>
        <ul className="list-unstyled">
          {/* <li className={`bar-text ${classes["my-files-sidebar-menu"]}`}>Query Menu</li> */}
          <li className={classes["sidebar-item"]}>
            <Link to={"/openquery/queries"} className={classes["content"]}>
              <span className={classes["text"]}>My Queries</span>
            </Link>
          </li>
          {remindersEnabled && (
            <li className={classes["sidebar-item"]}>
              <Link to={"/openquery/reminders"} className={classes["content"]}>
                <span className={classes["text"]}>My Reminders</span>
              </Link>
            </li>
          )}
          <li className={classes["sidebar-item"]}>
            <Link to={"/myfiles"} className={classes["content"]}>
              <span className={classes["text"]}>My Files</span>
            </Link>
          </li>
          <li className={classes["sidebar-item"]}>
            <Link to={"/openquery"} className={classes["content"]}>
              <span className={classes["text"]}>Create New Query</span>
            </Link>
          </li>
          <li className={classes["sidebar-item"]}>
            <Link to={"/dashboards"} className={classes["content"]}>
              <span className={classes["text"]}>Dashboards</span>
            </Link>
          </li>
          <li className={classes["sidebar-item"]}>
            <Link to={"/help"} className={classes["content"]}>
              <span className={classes["text"]}>Help Articles</span>
            </Link>
          </li>
          <li className={classes["sidebar-item"]}>
            <Link to={"/User"} className={classes["content"]}>
              <span className={classes["text"]}>My User</span>
            </Link>
          </li>
          <li
            className={`bar-text ${classes["my-files-sidebar-applications"]}`}
          >
            <span className={classes["text"]}>Applications</span>
          </li>
          <ApplicationList />
        </ul>
      </nav>
    </div>
  );
};

const AccountSidebar = ({ collapsed, onToggle, location }) => {
  return (
    <div
      className={`${classes["sidebar-wrapper"]} ${
        collapsed ? classes.collapsed : ""
      }`}
    >
      <HuronLogoSidebar collapsed={collapsed} />
      <span
        className={`${classes["toggle-icon"]} ${
          collapsed
            ? classes["expand-icon"]
            : classes["collapse-icon"]
        }`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        <CollapseIcon />
      </span>
      <nav
        id="sidebar"
        className={`${classes["sidebar-nav"]} ${classes["sidebar-openquery"]}`}
      >
        <ul className={`list-unstyled ${classes["menu-items-list"]}`}>
          <li
            className={`${classes["sidebar-item"]} ${
              location.pathname === "/dashboards/shared" ||
              location.state?.activeTab === "shared"
                ? classes["active-tab"]
                : ""
            }`}
          >
            <Link to="/dashboards/shared" className={classes.content}>
              <span className={classes.icon}>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    collapsed ? (
                      <Tooltip id="sidebar-tooltip">
                        Shared Dashboards
                      </Tooltip>
                    ) : (
                      <></>
                    )
                  }
                  trigger={collapsed ? ["hover", "focus"] : []}
                >
                  <SharedDashboard className={classes.svgIcon} />
                </OverlayTrigger>
              </span>

              {!collapsed && (
                <span className={classes.text}>Shared Dashboards</span>
              )}
            </Link>
          </li>
          <li
            className={`${classes["sidebar-item"]} ${
              location.pathname === "/dashboards/owned" ||
              location.state?.activeTab === "owned"
                ? classes["active-tab"]
                : ""
            }`}
          >
            <Link to="/dashboards/owned" className={classes.content}>
              <span className={classes.icon}>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    collapsed ? (
                      <Tooltip id="sidebar-tooltip">My Dashboards</Tooltip>
                    ) : (
                      <></>
                    )
                  }
                  trigger={collapsed ? ["hover", "focus"] : []}
                >
                  <MyDashboard className={classes.svgIcon} />
                </OverlayTrigger>
              </span>

              {!collapsed && (
                <span className={classes.text}>My Dashboards</span>
              )}
            </Link>
          </li>
        </ul>

        <ApplicationList collapsed={collapsed} />
      </nav>
    </div>
  );
};

const SidebarView = ({ loggedInUserProfile, collapsed, onToggle }) => {
  const location = useLocation();
  const isActive = useIsActive(location);
  const isMyQueriesActive = location.pathname === "/openquery/queries";
  const counts = loggedInUserProfile.accounts.reduce(
    (acc, account) => {
      if (
        account.role === "GlobalAdmin" ||
        account.role === "BrandAdmin" ||
        account.role === "admin"
      ) {
        acc.accounts += 1;
      }
      if (
        account.role === "GlobalAdmin" ||
        account.role === "BrandAdmin"
      ) {
        acc.brands += 1;
      }
      return acc;
    },
    {
      brands: 0,
      accounts: 0
    }
  );

  return (
    <Switch>
      <Route exact path="/">
        <AccountSidebar
          counts={counts}
          collapsed={collapsed}
          onToggle={onToggle}
        />
      </Route>
      <Route path={[...SIDEBAR_ROUTES.DASHBOARD]}>
        <AccountSidebar
          counts={counts}
          collapsed={collapsed}
          onToggle={onToggle}
          location={location}
          isActive={isActive}
        />
      </Route>
      <Route path="/openquery">
        <OpenQuerySidebar
          isMyQueriesActive={isMyQueriesActive}
          collapsed={collapsed}
          onToggle={onToggle}
        />
      </Route>
      <Route path="/queryvisualization/:visualizationId">
        {location.state?.dashboards ? (
          <AccountSidebar
            counts={counts}
            collapsed={collapsed}
            onToggle={onToggle}
            location={location}
            isActive={isActive}
          />
        ) : (
          <OpenQuerySidebar
            isMyQueriesActive={isMyQueriesActive}
            collapsed={collapsed}
            onToggle={onToggle}
          />
        )}
      </Route>
      <Route path="/aware">
        <MonitoringServicesSidebar
          collapsed={collapsed}
          onToggle={onToggle}
        />
      </Route>
      <Route path="/myfiles">
        <MyFilesSidebar />
      </Route>
      <Route path={COMMON_SIDEBAR_ROUTES}>
        <CommonSidebar
          collapsed={collapsed}
          onToggle={onToggle}
          isActive={isActive}
        />
      </Route>
    </Switch>
  );
};

SidebarView.defaultProps = {
  history: null
};

SidebarView.propTypes = {
  history: PropTypes.object
};

export default SidebarView;
